import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"
import {
  fieldProjectionV3Schema,
  fieldVisitProjectionV3Schema,
} from "./field-projection-v3.js"

const boundedLabel = z.string().trim().min(1).max(160)
const optionalDetail = z.string().trim().min(1).max(2_000).nullable()

export const fieldServiceOptionV1Schema = z.object({
  serviceId: entityIdSchema,
  displayName: boundedLabel,
  defaultQuantityMillis: z.number().int().positive().safe(),
}).strict()

export const fieldAssignmentDetailsV1Schema = z.object({
  customerDisplayName: boundedLabel,
  contactName: boundedLabel.nullable(),
  contactPhone: z.string().trim().min(3).max(40).nullable(),
  locationDisplayName: boundedLabel,
  addressLine1: boundedLabel,
  addressLine2: boundedLabel.nullable(),
  city: boundedLabel,
  region: z.string().trim().min(1).max(80),
  postalCode: z.string().trim().min(1).max(24),
  country: z.string().trim().min(2).max(80),
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
  accessInstructions: optionalDetail,
  hazards: optionalDetail,
  scope: z.string().trim().min(1).max(10_000),
  operationalInstructions: optionalDetail,
  serviceOptions: z.array(fieldServiceOptionV1Schema).min(1).max(50),
}).strict()

export const fieldVisitProjectionV4Schema = fieldVisitProjectionV3Schema.extend({
  assignment: fieldAssignmentDetailsV1Schema,
}).strict()

export const fieldProjectionV4Schema = z.object({
  ...fieldProjectionV3Schema.shape,
  responseVersion: z.literal(4),
  visits: z.array(fieldVisitProjectionV4Schema).max(100),
}).strict().superRefine((projection, context) => {
  const generated = Date.parse(projection.generatedAt)
  const expires = Date.parse(projection.expiresAt)
  if (expires <= generated || expires - generated > 15 * 60 * 1_000) {
    context.addIssue({
      code: "custom",
      message: "Field projection expiry must be within 15 minutes.",
      path: ["expiresAt"],
    })
  }
  const start = Date.parse(`${projection.horizonStartDate}T00:00:00Z`)
  const end = Date.parse(`${projection.horizonEndDate}T00:00:00Z`)
  if (end - start !== 2 * 24 * 60 * 60 * 1_000) {
    context.addIssue({
      code: "custom",
      message: "Field projection horizon must be today and the next day.",
      path: ["horizonEndDate"],
    })
  }
  if (
    new Set(projection.visits.map((visit) => visit.visitId)).size !==
    projection.visits.length
  ) {
    context.addIssue({
      code: "custom",
      message: "Field projection Visits must be unique.",
      path: ["visits"],
    })
  }
  if (
    projection.workShift &&
    !projection.relationships.some(
      (relationship) =>
        relationship.relationshipId === projection.workShift?.relationshipId &&
        relationship.legalEntityId === projection.workShift.legalEntityId,
    )
  ) {
    context.addIssue({
      code: "custom",
      message: "Active Work Shift must use a current relationship.",
      path: ["workShift"],
    })
  }
  if (
    projection.visitTime &&
    (!projection.workShift ||
      projection.visitTime.workShiftId !== projection.workShift.workShiftId)
  ) {
    context.addIssue({
      code: "custom",
      message: "Visit Time must be nested in the active Work Shift.",
      path: ["visitTime"],
    })
  }
  if (
    projection.visitTime &&
    !projection.relationships.some(
      (relationship) =>
        relationship.legalEntityId === projection.visitTime?.legalEntityId &&
        relationship.branchIds.includes(projection.visitTime.branchId),
    )
  ) {
    context.addIssue({
      code: "custom",
      message: "Visit Time must remain inside the current worker scope.",
      path: ["visitTime"],
    })
  }
})

export type FieldProjectionV4 = z.infer<typeof fieldProjectionV4Schema>
export type FieldVisitProjectionV4 = z.infer<typeof fieldVisitProjectionV4Schema>
export type FieldServiceOptionV1 = z.infer<typeof fieldServiceOptionV1Schema>
